import dataFetcher from "modules/dataFetcher";
import api from "services/api";
import ModalService from "services/modal";
import { keyedDataFetcher } from "modules/dataFetcher";
import { getCurrentProjectID } from "state/auth/selectors/common";
import { presentFilterTags } from "utils/presenters";
import store from "services/store";
import { PolicySchema } from "utils/schemas";
import ListActions from "modules/list/actions";

const getPolicyListingPayload = ({
  sortOrder,
  sortField,
  policyName: name,
  tags,
  policyType = [],
}) => {
  return {
    filter: {
      policyName: {
        contains: name || "",
      },
      tags: {
        eq: tags || [],
      },
      policyType,
    },
    sort: [
      {
        field: sortField || "lastModifiedTimestamp",
        order: sortOrder || "desc",
      },
    ],
  };
};

export const policiesFetcher = dataFetcher({
  selectors: ["policies"],
  async fetchData(_, query) {
    const { offset, limit, continue: continueToken, ...rest } = query;
    const continueQueryParam = continueToken
      ? `&continue=${continueToken}`
      : "";
    const payload = getPolicyListingPayload(rest);
    const response = api.post(
      `v1/dashboard/spcPolicies?limit=${limit}&offset=${offset}${continueQueryParam}`,
      payload
    );
    return response;
  },
});

export const policiesFiltersFetcher = dataFetcher({
  selectors: ["policiesListingFilters"],
  async fetchData() {
    const tagsResult = await store.dispatch(policiesTagsFetcher.fetch());
    return {
      policyName: {
        name: "policyName",
        displayName: "Name",
        type: "string",
      },
      tags: {
        name: "tags",
        displayName: "Tags",
        type: "string",
        options: presentFilterTags(tagsResult),
      },
      policyType: {
        name: "policyType",
        displayName: "Type",
        type: "string",
        options: [
          {
            value: "maintenance",
            name: "maintenance",
            label: "Maintenance",
            "data-qa": "maintenance",
          },
        ],
      },
    };
  },
});

const policiesTagsFetcher = dataFetcher({
  selectors: ["policies", "tags"],
  async fetchData(selectors, filters) {
    const currentProjectID = getCurrentProjectID(store.getState());
    let tags = policiesTagsCache
      .key(currentProjectID)
      .selector(store.getState())?.result;
    if (!tags) {
      tags = await store.dispatch(
        policiesTagsCache.key(currentProjectID).fetch()
      );
    }
    if (filters?.search) {
      return tags.filter((tag) =>
        tag.toLowerCase().includes(filters.search.toLowerCase())
      );
    }
    return tags;
  },
});

export const policiesTagsCache = keyedDataFetcher({
  selectors: ["policies", "tags", "cache"],
  async fetchData() {
    const response = await api.get("v1/spcPolicies/tags");
    return response.tags || [];
  },
});

export const policiesActions = new ListActions({
  hasPagination: true,
  persistFilters: true,
  schema: [PolicySchema],
  defaultQuery: {
    limit: 25,
  },
  initialQuery: () => {
    return {
      limit: 25,
    };
  },
  dataFetcher: policiesFetcher,
  filtersFetcher: policiesFiltersFetcher,
  filtersConfig: {
    data: {
      policyName: {
        componentType: "search",
        search: true,
        placeholder: "Filter by policy name",
        operator: "contains",
      },
      tags: {
        locked: true,
        componentType: "dropdown",
      },
      policyType: {
        locked: true,
        componentType: "checkbox",
      },
    },
    order: ["policyType", "tags"],
  },
});

export const maintenancePolicyScheduleModalService = new ModalService(
  "maintenancePolicySchedule"
);

export const maintenancePolicyScheduleDeleteModalService = new ModalService(
  "maintenancePolicyScheduleDelete"
);

export const wizardCloseModalService = new ModalService(
  "wizardCloseModalService"
);

export const deletePolicyModalService = new ModalService("deletePolicy");

export const inUseTemplatesModalService = new ModalService(
  "inUseTemplatesModalService"
);
